import { TOTAL_DEVICES } from '@app/ACMOpenShift/acmOverviewData';
import type { StatusTone } from '@app/components/StatusLabel';

export type FleetStatus = 'Successful' | 'Warning' | 'Running' | 'Failed';
export type FleetKind =
  | 'fitting'
  | 'warehouse'
  | 'store'
  | 'office'
  | 'kiosk'
  | 'plant'
  | 'uas'
  | 'billing'
  | 'popup';

export interface Fleet {
  id: string;
  name: string;
  kind: FleetKind;
  systemImage: string;
  deviceCount: number;
  status: FleetStatus;
}

export const FLEET_STATUS_TONE: Record<FleetStatus, StatusTone> = {
  Successful: 'success',
  Warning: 'warning',
  Running: 'info',
  Failed: 'danger',
};

const SYSTEM_IMAGES = [
  'RHEL 8.8',
  'RHEL 8.10',
  'RHEL 9.2',
  'RHEL 9.3',
  'RHEL 9.4',
  'RHEL 9.5',
  'RHEL 9.6',
  'RHEL 10',
  'RHEL 10.0',
  'RHEL for Edge 9.4',
  'RHEL for Edge 9.5',
  'RHEL for Edge 9.6',
  'MicroShift 4.17',
  'MicroShift 4.18',
  'RHCOS 4.16',
  'RHCOS 4.17',
];

const KIND_RANGE: Record<FleetKind, [number, number]> = {
  fitting: [11, 86],
  warehouse: [147, 812],
  store: [41, 427],
  office: [58, 356],
  kiosk: [4, 39],
  plant: [163, 704],
  uas: [19, 221],
  billing: [77, 538],
  popup: [6, 51],
};

const STATUS_OVERRIDES: Record<string, FleetStatus> = {
  'EU Drone Ground Rota': 'Failed',
  'EU Drone Ground Ramstein': 'Warning',
  'Billing Edge EU Central': 'Failed',
  'Billing Edge NA East': 'Running',
  'Shenzhen SMT': 'Failed',
  'Shenzhen DC': 'Warning',
  'Lagos Ikeja': 'Failed',
  'Pune Plant': 'Warning',
  'Frankfurt Airport Kiosk': 'Failed',
  "Chicago O'Hare Kiosk": 'Running',
  'Johannesburg DC': 'Warning',
  'Mexico City Reforma': 'Warning',
  'Core Edge Charlie Services': 'Running',
  'Fitting Room': 'Warning',
  'Warehouse': 'Successful',
  'Store': 'Failed',
  'Office': 'Running',
  'NYC Fifth Avenue': 'Successful',
  'Dallas Distribution': 'Successful',
  'Ohio Assembly': 'Running',
};

const SEEDS: { name: string; kind: FleetKind }[] = [
  { name: 'Fitting Room', kind: 'fitting' },
  { name: 'Madrid Fitting Room', kind: 'fitting' },
  { name: 'Barcelona Fitting Room', kind: 'fitting' },
  { name: 'Paris Haussmann Fitting', kind: 'fitting' },
  { name: 'London Oxford Fitting', kind: 'fitting' },
  { name: 'NYC Fifth Ave Fitting', kind: 'fitting' },
  { name: 'LA Beverly Fitting', kind: 'fitting' },
  { name: 'Tokyo Ginza Fitting', kind: 'fitting' },
  { name: 'Dubai Mall Fitting', kind: 'fitting' },
  { name: 'Singapore Orchard Fitting', kind: 'fitting' },
  { name: 'Warehouse', kind: 'warehouse' },
  { name: 'Dallas Distribution', kind: 'warehouse' },
  { name: 'Chicago DC', kind: 'warehouse' },
  { name: 'Rotterdam Hub', kind: 'warehouse' },
  { name: 'Singapore Port DC', kind: 'warehouse' },
  { name: 'Shenzhen DC', kind: 'warehouse' },
  { name: 'Melbourne DC', kind: 'warehouse' },
  { name: 'São Paulo DC', kind: 'warehouse' },
  { name: 'Johannesburg DC', kind: 'warehouse' },
  { name: 'Montreal DC', kind: 'warehouse' },
  { name: 'Hamburg Hub', kind: 'warehouse' },
  { name: 'Atlanta Cross-dock', kind: 'warehouse' },
  { name: 'Mumbai DC', kind: 'warehouse' },
  { name: 'Dubai Jebel Ali DC', kind: 'warehouse' },
  { name: 'Seoul DC', kind: 'warehouse' },
  { name: 'Mexico City DC', kind: 'warehouse' },
  { name: 'Store', kind: 'store' },
  { name: 'NYC Fifth Avenue', kind: 'store' },
  { name: 'Chicago Loop', kind: 'store' },
  { name: 'San Francisco Union Square', kind: 'store' },
  { name: 'Seattle Downtown', kind: 'store' },
  { name: 'Boston Back Bay', kind: 'store' },
  { name: 'Miami Brickell', kind: 'store' },
  { name: 'Toronto Eaton Centre', kind: 'store' },
  { name: 'Vancouver Robson', kind: 'store' },
  { name: 'Mexico City Reforma', kind: 'store' },
  { name: 'São Paulo Paulista', kind: 'store' },
  { name: 'London Oxford Street', kind: 'store' },
  { name: 'Paris Champs-Élysées', kind: 'store' },
  { name: 'Berlin Kudamm', kind: 'store' },
  { name: 'Madrid Gran Via', kind: 'store' },
  { name: 'Rome Condotti', kind: 'store' },
  { name: 'Amsterdam Kalverstraat', kind: 'store' },
  { name: 'Stockholm NK', kind: 'store' },
  { name: 'Warsaw Zlote Tarasy', kind: 'store' },
  { name: 'Dubai Mall', kind: 'store' },
  { name: 'Riyadh Kingdom Centre', kind: 'store' },
  { name: 'Johannesburg Sandton', kind: 'store' },
  { name: 'Lagos Ikeja', kind: 'store' },
  { name: 'Nairobi Westlands', kind: 'store' },
  { name: 'Mumbai Linking Road', kind: 'store' },
  { name: 'Delhi Connaught Place', kind: 'store' },
  { name: 'Singapore Orchard', kind: 'store' },
  { name: 'Tokyo Ginza', kind: 'store' },
  { name: 'Seoul Myeongdong', kind: 'store' },
  { name: 'Sydney Pitt Street', kind: 'store' },
  { name: 'Melbourne Chapel Street', kind: 'store' },
  { name: 'Auckland Queen Street', kind: 'store' },
  { name: 'Manila Makati', kind: 'store' },
  { name: 'Jakarta SCBD', kind: 'store' },
  { name: 'Bangkok Siam', kind: 'store' },
  { name: 'Ho Chi Minh D1', kind: 'store' },
  { name: 'Office', kind: 'office' },
  { name: 'Austin HQ', kind: 'office' },
  { name: 'Raleigh Campus', kind: 'office' },
  { name: 'London Canary Wharf', kind: 'office' },
  { name: 'Dublin EMEA HQ', kind: 'office' },
  { name: 'Bengaluru Tech Park', kind: 'office' },
  { name: 'Singapore APAC HQ', kind: 'office' },
  { name: 'Tokyo Marunouchi', kind: 'office' },
  { name: 'São Paulo LatAm HQ', kind: 'office' },
  { name: 'Dubai MENA HQ', kind: 'office' },
  { name: 'Cape Town Office', kind: 'office' },
  { name: 'Berlin Engineering', kind: 'office' },
  { name: 'Denver Regional', kind: 'office' },
  { name: 'NYC Herald Square Kiosk', kind: 'kiosk' },
  { name: "Chicago O'Hare Kiosk", kind: 'kiosk' },
  { name: 'London Heathrow Kiosk', kind: 'kiosk' },
  { name: 'Paris CDG Kiosk', kind: 'kiosk' },
  { name: 'Tokyo Haneda Kiosk', kind: 'kiosk' },
  { name: 'Dubai DXB Kiosk', kind: 'kiosk' },
  { name: 'Singapore Changi Kiosk', kind: 'kiosk' },
  { name: 'Frankfurt Airport Kiosk', kind: 'kiosk' },
  { name: 'Ohio Assembly', kind: 'plant' },
  { name: 'Stuttgart Plant', kind: 'plant' },
  { name: 'Monterrey Plant', kind: 'plant' },
  { name: 'Pune Plant', kind: 'plant' },
  { name: 'Penang Plant', kind: 'plant' },
  { name: 'Brno Plant', kind: 'plant' },
  { name: 'Guadalajara EMS', kind: 'plant' },
  { name: 'Shenzhen SMT', kind: 'plant' },
  { name: 'EU Drone Ground Ramstein', kind: 'uas' },
  { name: 'EU Drone Ground Rota', kind: 'uas' },
  { name: 'EU Drone Ground Aviano', kind: 'uas' },
  { name: 'EU Drone Ground Mildenhall', kind: 'uas' },
  { name: 'NA Drone Ground Nellis', kind: 'uas' },
  { name: 'NA Drone Ground Pendleton', kind: 'uas' },
  { name: 'APAC Drone Ground Kadena', kind: 'uas' },
  { name: 'APAC Drone Ground Darwin', kind: 'uas' },
  { name: 'Billing Edge NA East', kind: 'billing' },
  { name: 'Billing Edge NA West', kind: 'billing' },
  { name: 'Billing Edge EU Central', kind: 'billing' },
  { name: 'Billing Edge APAC', kind: 'billing' },
  { name: 'Billing Edge LATAM', kind: 'billing' },
  { name: 'Core Edge Charlie Services', kind: 'billing' },
  { name: 'Pop-up Boston Seaport', kind: 'popup' },
  { name: 'Pop-up Austin Domain', kind: 'popup' },
  { name: 'Pop-up London Kings Cross', kind: 'popup' },
];

function hash(value: string): number {
  let next = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    next = Math.imul(next ^ value.charCodeAt(i), 16777619);
  }
  return next >>> 0;
}

function unit(value: string): number {
  return hash(value) / 4294967296;
}

function pickImage(name: string, kind: FleetKind): string {
  const preferred =
    kind === 'uas' || kind === 'kiosk' || kind === 'popup'
      ? ['MicroShift 4.17', 'MicroShift 4.18', 'RHEL for Edge 9.4', 'RHEL for Edge 9.5', 'RHEL for Edge 9.6', 'RHCOS 4.16']
      : kind === 'plant' || kind === 'warehouse'
        ? ['RHEL 8.10', 'RHEL 9.2', 'RHEL 9.3', 'RHEL 9.4', 'RHEL 9.6', 'RHEL 10', 'RHCOS 4.17']
        : SYSTEM_IMAGES;
  return preferred[hash(`${name}:image`) % preferred.length];
}

function pickStatus(name: string): FleetStatus {
  if (STATUS_OVERRIDES[name]) {
    return STATUS_OVERRIDES[name];
  }
  const bucket = hash(`${name}:status`) % 100;
  if (bucket < 54) return 'Successful';
  if (bucket < 74) return 'Warning';
  if (bucket < 87) return 'Running';
  return 'Failed';
}

function rawCount(name: string, kind: FleetKind): number {
  const [min, max] = KIND_RANGE[kind];
  return min + Math.floor(unit(`${name}:count`) * (max - min + 1));
}

function normalizeCounts(counts: number[], total: number): number[] {
  const sum = counts.reduce((acc, count) => acc + count, 0);
  const scaled = counts.map((count) => Math.max(1, Math.round((count * total) / sum)));
  let remainder = total - scaled.reduce((acc, count) => acc + count, 0);
  const largestFirst = scaled
    .map((count, index) => ({ count, index }))
    .sort((a, b) => b.count - a.count);

  let cursor = 0;
  while (remainder !== 0) {
    const index = largestFirst[cursor % largestFirst.length].index;
    if (remainder > 0) {
      scaled[index] += 1;
      remainder -= 1;
    } else if (scaled[index] > 1) {
      scaled[index] -= 1;
      remainder += 1;
    }
    cursor += 1;
  }

  return scaled;
}

const deviceCounts = normalizeCounts(
  SEEDS.map((seed) => rawCount(seed.name, seed.kind)),
  TOTAL_DEVICES,
);

export const FLEETS: Fleet[] = SEEDS.map((seed, index) => ({
  id: String(index + 1),
  name: seed.name,
  kind: seed.kind,
  systemImage: pickImage(seed.name, seed.kind),
  deviceCount: deviceCounts[index],
  status: pickStatus(seed.name),
}));

export const TOTAL_FLEETS = FLEETS.length;
export const FLEET_DEVICE_TOTAL = FLEETS.reduce((sum, fleet) => sum + fleet.deviceCount, 0);

if (FLEET_DEVICE_TOTAL !== TOTAL_DEVICES) {
  throw new Error(`Fleet device counts (${FLEET_DEVICE_TOTAL}) must equal TOTAL_DEVICES (${TOTAL_DEVICES})`);
}

if (TOTAL_FLEETS !== 108) {
  throw new Error(`Expected 108 fleets, got ${TOTAL_FLEETS}`);
}

export const getFleetByName = (name: string): Fleet | undefined => FLEETS.find((fleet) => fleet.name === name);
